'use client';

import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Loader2 } from 'lucide-react';
import {
  getProgramTemplateById,
  type AgeGroup,
  type ProgramTemplateProviderId,
} from '@court-sync/shared';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui';
import { programService } from '../../services/program';
import { ProgramTemplateLegalNotice } from './ProgramTemplateLegalNotice';
import { ProgramPathwayParallelBranchesTip } from './ProgramPathwayParallelBranchesTip';
import { resolveSettingsPath } from '@/lib/settingsTabRoutes';

type WizardStep = 'preview' | 'mapping' | 'review';

const STEP_TITLES: Record<WizardStep, string> = {
  preview: 'Preview stages',
  mapping: 'Map age groups',
  review: 'Review and create',
};

export function ProgramTemplateImportWizard({
  isOpen,
  onClose,
  providerId,
  academyId,
  ageGroups,
  ageGroupsLoading = false,
  onCreated,
  onBackToChoice,
}: {
  isOpen: boolean;
  onClose: () => void;
  providerId: ProgramTemplateProviderId;
  academyId: string;
  ageGroups: AgeGroup[];
  ageGroupsLoading?: boolean;
  onCreated: () => void | Promise<void>;
  onBackToChoice: () => void;
}) {
  const [step, setStep] = useState<WizardStep>('preview');
  const [ageGroupByStage, setAgeGroupByStage] = useState<Record<string, string>>({});
  const [creating, setCreating] = useState(false);

  const template = useMemo(() => getProgramTemplateById(providerId) ?? null, [providerId]);

  useEffect(() => {
    if (!isOpen || !template) return;
    setStep('preview');
    const initial: Record<string, string> = {};
    for (const s of template.stages) {
      initial[s.stageKey] = '';
    }
    setAgeGroupByStage(initial);
    setCreating(false);
  }, [isOpen, template, providerId]);

  const handleClose = () => {
    onClose();
  };

  const goNext = () => {
    if (step === 'preview') setStep('mapping');
    else if (step === 'mapping') setStep('review');
  };

  const goBack = () => {
    if (step === 'preview') {
      handleClose();
      onBackToChoice();
      return;
    }
    if (step === 'mapping') setStep('preview');
    else if (step === 'review') setStep('mapping');
  };

  const handleCreate = async () => {
    if (!template) return;
    setCreating(true);
    try {
      const stageMappings = template.stages.map((s) => ({
        stageKey: s.stageKey,
        ageGroupId: ageGroupByStage[s.stageKey] || null,
      }));
      await programService.importProgramTemplate(academyId, {
        templateId: providerId,
        stageMappings,
      });
      toast.success(`Created ${template.stages.length} programs from ${template.templateName}`);
      await onCreated();
      handleClose();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to create pathway');
    } finally {
      setCreating(false);
    }
  };

  if (!template) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={STEP_TITLES[step]} maxWidth="3xl">
      <div className="space-y-6">
        {step === 'preview' && (
          <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
            <ProgramTemplateLegalNotice />
            {template.frameworkNote ? (
              <p className="text-sm text-gray-600 dark:text-gray-400 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3 border border-gray-200 dark:border-gray-700">
                {template.frameworkNote}
              </p>
            ) : null}
            <ol className="space-y-4 list-none m-0 p-0">
              {template.stages.map((s, index) => (
                <li
                  key={s.stageKey}
                  className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-gray-50/50 dark:bg-gray-800/30"
                >
                  <div className="flex items-start gap-2">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-semibold text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">{s.name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {s.recommendedAgeRangeLabel} · {s.skillLevel}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 line-clamp-3">
                        {s.description}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {s.targetSkills.length} target skills
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {step === 'mapping' && (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Optionally link each stage to an academy age group. Choose &quot;None&quot; to leave
              unassigned. You can add or edit age groups anytime in{' '}
              <Link
                to={resolveSettingsPath('general')}
                className="text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
              >
                Academy Settings → Age Groups
              </Link>
              .
            </p>
            {ageGroupsLoading ? (
              <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin shrink-0" aria-hidden />
                Loading age groups…
              </p>
            ) : ageGroups.length === 0 ? (
              <p className="text-sm text-amber-700 dark:text-amber-300 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3">
                No age groups configured yet. You can still create this pathway and assign age groups
                later in program settings or in Academy Settings → Age Groups.
              </p>
            ) : null}
            <div className="space-y-3 max-h-[45vh] overflow-y-auto">
              {template.stages.map((s) => (
                <div
                  key={s.stageKey}
                  className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 rounded-lg border border-gray-200 dark:border-gray-700 p-3"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {s.name}
                    </p>
                    <p className="text-xs text-gray-500">{s.recommendedAgeRangeLabel}</p>
                  </div>
                  <select
                    className="input w-full sm:w-56 shrink-0"
                    value={ageGroupByStage[s.stageKey] ?? ''}
                    onChange={(e) =>
                      setAgeGroupByStage((prev) => ({ ...prev, [s.stageKey]: e.target.value }))
                    }
                    aria-label={`Age group for ${s.name}`}
                  >
                    <option value="">None</option>
                    {ageGroups.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 'review' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              You are about to create <strong>{template.stages.length}</strong> linked programs for
              the <strong>{template.templateName}</strong>.
            </p>
            <ul className="text-sm space-y-2 rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-200 dark:divide-gray-700">
              {template.stages.map((s) => {
                const agId = ageGroupByStage[s.stageKey];
                const agLabel = agId
                  ? ageGroups.find((g) => g.id === agId)?.label ?? 'Selected group'
                  : 'None';
                return (
                  <li key={s.stageKey} className="flex justify-between gap-4 px-3 py-2">
                    <span className="text-gray-900 dark:text-white font-medium truncate">
                      {s.name}
                    </span>
                    <span className="text-gray-500 shrink-0">{agLabel}</span>
                  </li>
                );
              })}
            </ul>
            <ProgramPathwayParallelBranchesTip />
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Created programs remain editable after setup.
            </p>
          </div>
        )}

        <div className="flex justify-between gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
          <div>
            <Button type="button" variant="outline" onClick={goBack} disabled={creating}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </div>
          <div className="flex gap-2">
            {step === 'review' ? (
              <Button type="button" onClick={() => void handleCreate()} disabled={creating}>
                {creating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4 mr-1" /> Create
                  </>
                )}
              </Button>
            ) : (
              <Button type="button" onClick={goNext}>
                Continue <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
